import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';

/**
 * Reusable drop zone · drag-and-drop or click-to-browse, multi-file by
 * default. Calls `onFiles(File[])` once per drop or pick. Visual state
 * brightens when the user drags files over it.
 *
 * Use `compact` for a thin inline strip (e.g. inside a card),
 * default for a larger upload area.
 */
export default function FileDropZone({
  onFiles, accept, multiple = true, disabled = false, compact = false,
  label = 'Glissez vos fichiers ici', sublabel = 'ou cliquez pour parcourir',
  busyLabel = null,
}) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const dispatch = (fileList) => {
    if (!fileList || !fileList.length) return;
    onFiles(Array.from(fileList));
  };

  const onDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setOver(true);
  };
  const onDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    setOver(false);
  };
  const onDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    setOver(false);
    dispatch(e.dataTransfer.files);
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-disabled={disabled}
      className={[
        'group rounded-md border-2 border-dashed text-center cursor-pointer transition outline-none',
        compact ? 'px-3 py-2.5 text-[13px]' : 'px-6 py-6',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        over
          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))]'
          : 'border-[hsl(var(--rule-strong))] bg-[hsl(var(--bg-subtle))] hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--surface-2))]',
        'focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]',
      ].join(' ')}
    >
      <input
        ref={inputRef} type="file" className="hidden"
        accept={accept} multiple={multiple} disabled={disabled}
        onChange={(e) => { dispatch(e.target.files); e.target.value = ''; }}
      />
      <div className={`flex items-center ${compact ? 'gap-2 justify-start' : 'gap-3 justify-center'}`}>
        <Upload className={compact ? 'h-4 w-4 text-[hsl(var(--text-soft))]' : 'h-5 w-5 text-[hsl(var(--text-soft))]'} />
        <div className={compact ? 'flex items-baseline gap-2' : 'space-y-0.5'}>
          <div className={compact ? 'text-[14px]' : 'text-[15px] font-medium'}>
            {busyLabel || label}
          </div>
          {!busyLabel && sublabel && (
            <div className={compact ? 'text-[12px] text-[hsl(var(--text-muted))]' : 'text-[13px] text-[hsl(var(--text-muted))]'}>
              {sublabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
